import { useCallback, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { verifyPavAssetPhysically } from '../../actions/service_request';
import { getSavedAssetApiId } from './pavAssetUtils';

// Shared physical-verification state for one ticket. Only one decision is in
// flight at a time. Decided assets are read back from the redux map the saga
// writes, so closing and reopening a verification screen still shows the
// decision even when the refreshed ticket has not picked the flag up yet. The
// in-flight guard is a ref so `verifyAsset` stays referentially stable and
// memoized cards do not re-render on every decision.
export default function usePavAssetVerification(ticketId) {
    const dispatch = useDispatch();
    const decisions = useSelector((state) => state.serviceRequest.pavAssetVerifications);
    const [pending, setPending] = useState(null);
    const pendingRef = useRef(null);

    const verifyAsset = useCallback(({ assetKey, assetId, milestoneId, isVerified, remark }) => {
        if (!ticketId || !milestoneId || !assetId || pendingRef.current) return;
        pendingRef.current = assetKey;
        setPending({ assetKey, isVerified });
        dispatch(verifyPavAssetPhysically({
            ticketId,
            milestoneId,
            assetId,
            isVerified,
            remark,
            formName: `pav_physical_verification_${assetId}`,
            onSuccess: () => {
                pendingRef.current = null;
                setPending(null);
            },
            onError: () => {
                pendingRef.current = null;
                setPending(null);
            },
        }));
    }, [dispatch, ticketId]);

    const getAssetOutcome = useCallback((asset) => {
        const assetId = getSavedAssetApiId(asset);
        return assetId ? decisions?.[assetId] : undefined;
    }, [decisions]);

    const getPendingDecision = useCallback(
        (assetKey) => (pending?.assetKey === assetKey ? pending.isVerified : undefined),
        [pending]
    );

    return { verifyAsset, getAssetOutcome, getPendingDecision };
}
